import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Link, RouterProvider } from 'react-router';
import { Layout } from './components/Layout';
import { Empty } from './components/ui';
import { ApiError } from './lib/api';
import { Admin } from './pages/Admin';
import { Alarms } from './pages/Alarms';
import { ApiKeys } from './pages/ApiKeys';
import { Connect } from './pages/Connect';
import { Invite } from './pages/Invite';
import { Members } from './pages/Members';
import { Plant } from './pages/Plant';
import { PlantAlarms } from './pages/PlantAlarms';
import { PlantCharts } from './pages/PlantCharts';
import { PlantDevices } from './pages/PlantDevices';
import { PlantOverview } from './pages/PlantOverview';
import { Plants } from './pages/Plants';
import { SignIn } from './pages/SignIn';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 4xx won't fix itself by retrying
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

function NotFound() {
  return <Empty>Nothing here. <Link to="/" className="text-accent underline">Back to plants</Link></Empty>;
}

const router = createBrowserRouter([
  { path: '/sign-in', element: <SignIn /> },
  { path: '/invite/:id', element: <Invite /> },
  {
    element: <Layout />,
    children: [
      { index: true, element: <Plants /> },
      {
        path: 'plants/:slug',
        element: <Plant />,
        children: [
          { index: true, element: <PlantOverview /> },
          { path: 'charts', element: <PlantCharts /> },
          { path: 'alarms', element: <PlantAlarms /> },
          { path: 'devices', element: <PlantDevices /> },
        ],
      },
      { path: 'alarms', element: <Alarms /> },
      { path: 'members', element: <Members /> },
      { path: 'api-keys', element: <ApiKeys /> },
      { path: 'connect', element: <Connect /> },
      { path: 'admin', element: <Admin /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
